// Create or edit a feed (the basic editor): its description and keywords, what it has learned, buyer rules,
// the kinds of work, buying paths and bidder groups (small business, vehicle holders, consortium members) it
// leaves out, sole-source visibility, and who can see it. The search that narrows it is edited on the advanced
// search page (Advanced.tsx, ?feed=ID).
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router";
import { api, errorText } from "../api/client";
import type { Feed, FeedKeywords } from "../api/types";
import { useUI } from "../app/context";
import { keys, useSession, useRefreshLists } from "../app/queries";
import { GroupPicker } from "../components/GroupPicker";
import { Modal } from "../components/Modal";

interface ShapeOption {
  value: string;
  label: string;
  hint?: string;
  sub?: string;
  count?: number;
  excluded: boolean;
}
type Kind = "work" | "path" | "who";
type Leave = Record<Kind, Set<string>>;
interface ShapeOptions {
  total: number | null;
  work: ShapeOption[];
  path: ShapeOption[];
  who: ShapeOption[];
  named?: { count: number; excluded: boolean };
  dow_only?: { count: number; on: boolean };
}
interface Member {
  id: number;
  name: string;
  disabled: number | boolean;
}

export function FeedEditor({ feed, open, onClose, focus }: { feed: Feed | null; open: boolean; onClose: () => void; focus?: "shape" }) {
  return (
    <Modal open={open} id="editor" onClose={onClose}>
      <EditorForm key={feed?.id ?? "new"} feed={feed} onClose={onClose} focus={focus} />
    </Modal>
  );
}

function EditorForm({ feed: f, onClose, focus }: { feed: Feed | null; onClose: () => void; focus?: "shape" }) {
  const me = useSession();
  const client = useQueryClient();
  const refresh = useRefreshLists();
  const navigate = useNavigate();
  const { showToast } = useUI();
  const [name, setName] = useState(f?.name ?? "");
  const [description, setDescription] = useState(f?.description ?? "");
  const [sole, setSole] = useState(!!f?.show_sole_source);
  const [visibility, setVisibility] = useState<Feed["visibility"]>(f?.visibility ?? "private");
  const [shared, setShared] = useState<Set<number>>(new Set(f?.shared_with ?? []));
  const [terms, setTerms] = useState<FeedKeywords["terms"]>(f?.keywords.terms ?? []);
  const [examples, setExamples] = useState(!!f?.keywords.examples);
  const [leave, setLeave] = useState<Leave | null>(null);
  const [named, setNamed] = useState<boolean | null>(null);  // show buys of a named product
  const [dowOnly, setDowOnly] = useState<boolean | null>(null);  // leave out buyers outside DoW and NATO
  const shapeRef = useRef<HTMLFieldSetElement>(null);
  const members = useQuery({ queryKey: ["members"], queryFn: () => api<{ members: Member[] }>("/api/members").then((d) => d.members) });
  const learning = useQuery({
    queryKey: ["feed", f?.id, "learning"],
    queryFn: () => api<{ marks: number; exclusions: number; rules: { id: number; value: string }[] }>(`/api/feeds/${f!.id}/learning`),
    enabled: !!f,
  });
  const counts = useQuery({
    queryKey: ["feed", f?.id, "keywords"],
    queryFn: () =>
      api<{ total: number; terms: { term: string; count: number; alone: number }[]; combos: { name: string; count: number }[] }>(`/api/feeds/${f!.id}/keywords`),
    enabled: !!f,
  });
  const shape = useQuery({
    queryKey: ["feed", f?.id ?? "new", "shape-options"],
    queryFn: async (): Promise<ShapeOptions> => {
      if (f) return api<ShapeOptions>(`/api/feeds/${f.id}/shape`);
      const o = await api<Record<Kind, ShapeOption[]>>("/api/search/options");
      const fresh = (xs: ShapeOption[]) => xs.map((x) => ({ ...x, excluded: false }));
      return { total: null, work: fresh(o.work), path: fresh(o.path), who: fresh(o.who) };
    },
  });
  useEffect(() => {
    if (shape.data && named === null) {
      setNamed(!shape.data.named?.excluded);
      setDowOnly(!!shape.data.dow_only?.on);
    }
  }, [shape.data, named]);
  useEffect(() => {
    if (shape.data && !leave)
      setLeave({
        work: new Set(shape.data.work.filter((o) => o.excluded).map((o) => o.value)),
        path: new Set(shape.data.path.filter((o) => o.excluded).map((o) => o.value)),
        who: new Set(shape.data.who.filter((o) => o.excluded).map((o) => o.value)),
      });
  }, [shape.data, leave]);
  useEffect(() => {
    if (focus === "shape") shapeRef.current?.scrollIntoView({ block: "start" });
  }, [focus, shape.data]);
  const others = (members.data ?? []).filter((m) => m.id !== me.user.id && !m.disabled);
  const shapeBody = () => ({
    work: [...(leave?.work ?? [])],
    path: [...(leave?.path ?? [])],
    who: [...(leave?.who ?? [])],
    named: named === false,
    dow_only: !!dowOnly,
  });

  const keywords: FeedKeywords = { terms, combos: f?.keywords.combos ?? [], examples };
  const save = async () => {
    const body = { name: name.trim(), description: description.trim(), visibility, shared_with: [...shared], show_sole_source: sole, keywords };
    if (!body.name) return;
    try {
      if (f) {
        await api(`/api/feeds/${f.id}`, { method: "PATCH", body });
        await api(`/api/feeds/${f.id}/shape`, { method: "PUT", body: shapeBody() });
        await client.invalidateQueries({ queryKey: ["feed", f.id] });
        onClose();
        refresh();
        showToast("Feed saved.");
      } else {
        const r = await api<{ id: number }>("/api/feeds", { method: "POST", body });
        const s = shapeBody();
        if (s.work.length || s.path.length || s.who.length || s.named || s.dow_only) await api(`/api/feeds/${r.id}/shape`, { method: "PUT", body: s });
        onClose();
        await client.invalidateQueries({ queryKey: keys.feeds });
        navigate(`/feeds/${r.id}?view=all`);
        showToast("Feed created. Mark records Pursue or Watch to teach it.");
      }
    } catch (err) {
      showToast(errorText(err));
    }
  };
  const remove = async () => {
    if (!f || !confirm(`Delete ${f.name}? Its marks, exclusions and rules go with it.`)) return;
    await api(`/api/feeds/${f.id}`, { method: "DELETE" });
    onClose();
    await client.invalidateQueries({ queryKey: keys.feeds });
    navigate("/brief");
    showToast(`Deleted ${f.name}.`);
  };
  const removeRule = async (id: number) => {
    await api(`/api/feeds/${f!.id}/rules/${id}`, { method: "DELETE" });
    await client.invalidateQueries({ queryKey: ["feed", f!.id, "learning"] });
    await refresh();
  };
  const toggle = (kind: Kind, value: string, on: boolean) => {
    if (!leave) return;
    const next = { ...leave, [kind]: new Set(leave[kind]) };
    if (on) next[kind].delete(value);
    else next[kind].add(value);
    setLeave(next);
  };
  // The vehicle and consortium pickers each own their part of the left-out bidder groups.
  const setGroups = (prefix: string, chosen: Set<string>) => {
    if (!leave) return;
    setLeave({ ...leave, who: new Set([...[...leave.who].filter((g) => !g.startsWith(prefix)), ...chosen]) });
  };
  const groups = (prefix: string) => (shape.data?.who ?? []).filter((o) => o.value.startsWith(prefix));
  const chosenGroups = (prefix: string) => new Set([...(leave?.who ?? [])].filter((g) => g.startsWith(prefix)));
  const l = learning.data;
  const opt = (kind: Kind, o: ShapeOption) => (
    <label key={o.value} className="shape-opt">
      <input type="checkbox" data-shape-name={kind} value={o.value} checked={!leave?.[kind].has(o.value)} onChange={(e) => toggle(kind, o.value, e.target.checked)} />
      <span>
        {o.label}
        {o.hint ? <small>{o.hint}</small> : null}
      </span>
      {shape.data?.total != null ? <span className="fc">{o.count}</span> : null}
    </label>
  );
  return (
    <form
      method="dialog"
      onSubmit={(e) => {
        e.preventDefault();
        save();
      }}
    >
      <div className="dlg">
        <div className="ed-head">
          <h2 id="ed-title">{f ? "Edit feed" : "New feed"}</h2>
          {f ? (
            <Link className="text-btn" id="ed-advanced" to={`/advanced?feed=${f.id}`} onClick={onClose}>
              Advanced: edit its search →
            </Link>
          ) : null}
        </div>
        <p>Describe what belongs in this feed. GovChew ranks new records by how closely they match your description and the records you've marked.</p>
        <label className="field">
          <span>Name</span>
          <input type="text" id="ed-name" maxLength={120} required autoFocus={focus !== "shape"} value={name} onChange={(e) => setName(e.target.value)} />
        </label>
        <label className="field">
          <span>What belongs here</span>
          <textarea id="ed-desc" rows={4} maxLength={2000} value={description} onChange={(e) => setDescription(e.target.value)} />
          <small>Pursue and Watch teach it what you want. The choices below leave out what you don't.</small>
        </label>
        <KeywordField terms={terms} onChange={setTerms} counts={counts.data} />
        {f?.keywords.combos.length ? (
          <div className="field">
            <span>Keyword combinations</span>
            <small>
              {f.keywords.combos.map((c) => `${c.name}: a title naming ${c.context.slice(0, 3).join(", ")}… with ${c.capabilities.slice(0, 3).join(", ")}…`).join("; ")}
            </small>
          </div>
        ) : null}
        {f?.examples_from ? (
          <label className="check">
            <input type="checkbox" id="ed-examples" checked={examples} onChange={(e) => setExamples(e.target.checked)} />
            <span>
              Also records similar to the “{f.examples_from}” examples
              <small className="muted" style={{ display: "block", color: "var(--ink-3)" }}>
                Sentences describing this line of work, set for the team; they bring in records that use none of the keywords.
              </small>
            </span>
          </label>
        ) : null}
        {f ? (
          <div className="field" id="ed-query-field">
            <span>Narrowed by</span>
            {f.query ? <code className="query-box">{f.query}</code> : <small>No search yet: everything the description and keywords bring in stays.</small>}
            <small>
              <Link to={`/advanced?feed=${f.id}`} onClick={onClose}>
                {f.query ? "Change it in advanced search →" : "Narrow it with advanced search →"}
              </Link>{" "}
              Keep to R&amp;D, leave out buyers, require words, and preview what a change does.
            </small>
          </div>
        ) : null}
        {f ? (
          <>
            <div className="field" id="ed-learned-field">
              <span>Learned from</span>
              <small id="ed-learned">
                {l ? (l.marks || l.exclusions ? `${l.marks} marked, ${l.exclusions} excluded.` : "Nothing yet. Pursue and Watch records to teach it.") : ""}
              </small>
            </div>
            {l?.rules.length ? (
              <div className="field" id="ed-rules-field">
                <span>Always leave out</span>
                <div className="rules" id="ed-rules">
                  {l.rules.map((r) => (
                    <button key={r.id} type="button" data-rule={r.id} title="Remove this rule" onClick={() => removeRule(r.id)}>
                      From {r.value} ×
                    </button>
                  ))}
                </div>
              </div>
            ) : null}
          </>
        ) : null}
        <fieldset className="field shape-field" ref={shapeRef}>
          <legend>Kinds of work</legend>
          <small>Untick what you don't pursue. It stays on topic but out of this feed. Records whose kind of work isn't stated always stay.</small>
          <div className="shape-opts" id="ed-work">
            {shape.data?.work.map((o) => opt("work", o))}
          </div>
        </fieldset>
        <fieldset className="field shape-field">
          <legend>How it's bought</legend>
          <div className="shape-opts" id="ed-path">
            {shape.data?.path.map((o) => opt("path", o))}
            <label className="shape-opt">
              <input type="checkbox" id="ed-named" checked={named !== false} onChange={(e) => setNamed(e.target.checked)} />
              <span>
                Buys of a named product
                <small>Licenses, renewals and support for a product the buyer names (Tanium, ArcGIS, …)</small>
              </span>
              {shape.data?.total != null ? <span className="fc">{shape.data.named?.count ?? 0}</span> : null}
            </label>
          </div>
        </fieldset>
        <fieldset className="field shape-field">
          <legend>Who can bid</legend>
          <small>A record leaves if any group it belongs to is left out: an SBIR is also small-business work.</small>
          <div className="shape-opts" id="ed-who">
            {shape.data?.who.filter((o) => !o.value.includes(":")).map((o) => opt("who", o))}
          </div>
          <div className="picker-field">
            <span>Leave out orders under these contracts</span>
            <GroupPicker
              label="Contract vehicles to leave out"
              placeholder="Search vehicles: SeaPort, OASIS+, SEWP…"
              options={groups("vehicle:")}
              chosen={chosenGroups("vehicle:")}
              onChange={(c) => setGroups("vehicle:", c)}
              showCounts={shape.data?.total != null}
              removeTitle="Stop leaving these out"
            />
          </div>
          <div className="picker-field">
            <span>Leave out calls through these consortia</span>
            <GroupPicker
              label="Consortia to leave out"
              placeholder="Search consortia: NSTIC, TReX, MTEC…"
              options={groups("consortium:")}
              chosen={chosenGroups("consortium:")}
              onChange={(c) => setGroups("consortium:", c)}
              showCounts={shape.data?.total != null}
              removeTitle="Stop leaving these out"
            />
          </div>
        </fieldset>
        <div className="field">
          <span>Also show</span>
          <label className="check">
            <input type="checkbox" id="ed-sole" checked={sole} onChange={(e) => setSole(e.target.checked)} />
            <span>
              Notices of intent to sole source
              <small className="muted" style={{ display: "block", color: "var(--ink-3)" }}>
                Hidden by default: they announce an award to a named vendor and rarely invite bids.
              </small>
            </span>
          </label>
          <label className="check">
            <input type="checkbox" id="ed-dow-only" checked={!!dowOnly} onChange={(e) => setDowOnly(e.target.checked)} />
            <span>
              Only DoW and NATO buyers{shape.data?.dow_only?.count ? ` (leaves out ${shape.data.dow_only.count} here)` : ""}
              <small className="muted" style={{ display: "block", color: "var(--ink-3)" }}>
                Leaves out NIH, CDC, FDA, VA and other agencies' records.
              </small>
            </span>
          </label>
        </div>
        <div className="field">
          <span>Who can see it</span>
          <div className="opts" id="ed-share">
            {(
              [
                ["private", "Only me", ""],
                ["people", "Specific people", "They can follow it; only you and admins can change it."],
                ["tenant", `Everyone at ${me.tenant?.name}`, "Anyone can follow it."],
              ] as [Feed["visibility"], string, string][]
            ).map(([v, label, sub]) => (
              <label key={v}>
                <input type="radio" name="share" value={v} checked={visibility === v} onChange={() => setVisibility(v)} />
                <span>
                  {label}
                  {sub ? <small>{sub}</small> : null}
                </span>
              </label>
            ))}
          </div>
          {visibility === "people" ? (
            <div className="people" id="ed-people">
              {others.length ? (
                others.map((m) => (
                  <label key={m.id}>
                    <input
                      type="checkbox"
                      value={m.id}
                      checked={shared.has(m.id)}
                      onChange={(e) => {
                        const next = new Set(shared);
                        if (e.target.checked) next.add(m.id);
                        else next.delete(m.id);
                        setShared(next);
                      }}
                    />
                    {m.name}
                  </label>
                ))
              ) : (
                <small>No one else is in your organization yet.</small>
              )}
            </div>
          ) : null}
        </div>
      </div>
      <div className="foot">
        {f ? (
          <button className="btn danger left" type="button" id="ed-delete" onClick={remove}>
            Delete feed
          </button>
        ) : null}
        <button className="btn" type="button" value="cancel" onClick={onClose}>
          Cancel
        </button>
        <button className="btn primary" value="ok">
          Save feed
        </button>
      </div>
    </form>
  );
}

/** The feed's keywords as chips: how many records each brings in (and how many it brings in alone), a toggle for
 * "only beside another keyword", and a box to add more. */
function KeywordField({
  terms,
  onChange,
  counts,
}: {
  terms: FeedKeywords["terms"];
  onChange: (t: FeedKeywords["terms"]) => void;
  counts?: { terms: { term: string; count: number; alone: number }[] };
}) {
  const [text, setText] = useState("");
  const count = (t: string) => counts?.terms.find((x) => x.term === t);
  const add = () => {
    const fresh = text
      .split(",")
      .map((x) => x.trim().toLowerCase())
      .filter((x) => x && !terms.some((t) => t.term.toLowerCase() === x));
    if (fresh.length) onChange([...terms, ...fresh.map((term) => ({ term, weak: false }))]);
    setText("");
  };
  return (
    <div className="field" id="ed-keywords">
      <span>Keywords</span>
      <small>
        A keyword in a title brings a record in; in a description it takes two. Click a keyword to make it count only beside another one: good for common words like
        “clinical”.
      </small>
      <div className="kw-chips">
        {terms.map((t, i) => {
          const c = count(t.term);
          return (
            <span key={t.term} className={`kw ${t.weak ? "weak" : ""}`}>
              <button
                type="button"
                className="kw-t"
                aria-pressed={t.weak}
                title={t.weak ? "Counts only beside another keyword. Click to let it count alone." : "Click to make it count only beside another keyword"}
                onClick={() => onChange(terms.map((x, j) => (j === i ? { ...x, weak: !x.weak } : x)))}
              >
                {t.term}
                {t.weak ? <small> + another</small> : null}
                {c ? (
                  <span className="kw-n" title={`${c.count} of the feed's records name it; it alone brings in ${c.alone}`}>
                    {c.count}
                    {c.alone ? ` · ${c.alone} alone` : ""}
                  </span>
                ) : null}
              </button>
              <button type="button" className="kw-x" aria-label={`Remove ${t.term}`} onClick={() => onChange(terms.filter((_, j) => j !== i))}>
                ×
              </button>
            </span>
          );
        })}
      </div>
      <input
        type="text"
        id="ed-kw-input"
        placeholder="Add keywords, separated by commas"
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            add();
          }
        }}
        onBlur={add}
      />
    </div>
  );
}
